import { FormikValues } from 'formik';
import updateLocale from 'dayjs/plugin/updateLocale';
import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import {
  ACTIONS_KEY,
  LOCKED_KEY,
  TIMESTAMP_KEY,
  TYPE_PRIVATE_ACTION,
  VEHICLE_KEY,
} from './constants';
import showMessage from '../Toastr/ToastService';
import { IOption } from '../Forms/CmdField';
import PermissionsService, {
  PERMISSION_PERIOD_LOCK_ADMIN,
  PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT,
  PERMISSION_RIDE_INFO_TAGS_ASSIGMENT,
} from '../../Services/PermissionsService';
import JSONValidationService from '../../Services/JSONValidationService';
import TripsOverviewDataSchema from './TripsOverviewDataSchema.json';
import TripsOverviewSettingsSchema from './TripsOverviewSettingsSchema.json';
import { getDayjsLocale, getDefaultLanguage } from '../../Services/LanguageService';
import { IVehicles } from '../Vehicles/types';
import {
  ICheckedTableItems,
  IEndpoitData,
  IFilterTable,
  IFilterTripsOverview,
  IInfoRecord,
  IIsActiveAdvanceFilter,
  ITableSettingsData,
  ITableSettingsResponse,
  ITableSummariesFilter,
  ITagItem,
  ITripsOverViewRecord,
  ITripsOverViewSummary,
  ITripsOverviewChipOption,
  ITripsOverviewColumn,
} from './interfaces';
import { deleteRequest, getRequest, postRequest, putRequest } from '../../Services/HttpService';

const IS_LOCKED = 'locked';
const IS_RESTRICTED = 'restricted';

const getLockedRideBoolean = (locked: string, customerId: number, type: string) => {
  return (
    locked === IS_LOCKED ||
    (locked === IS_RESTRICTED &&
      !PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK_ADMIN, [type], customerId))
  );
};

const getActiveFilters = (filters: string[]) => {
  const activeFilters: string[] = [];
  if (!filters.includes('alarm')) {
    activeFilters.push('alarm');
  }
  if (!filters.includes('event')) {
    activeFilters.push('event');
  }
  if (!filters.includes('waypoint')) {
    activeFilters.push('waypoint');
  }
  return activeFilters;
};

const getEventsQuery = (information: string, filters: string[]) => {
  let query = '';
  const activeFilters = getActiveFilters(filters);
  activeFilters.forEach((item, key) => {
    if (key === 0) {
      query = `?entityType[]=${item}`;
    } else {
      query += `&entityType[]=${item}`;
    }
  });
  if (information !== '') {
    query = activeFilters.length === 0 ? `?group=${information}` : `${query}&group=${information}`;
  }
  return query;
};

export const getRidebookData = async (data: IEndpoitData, signal?: AbortSignal): Promise<any> => {
  const opts = { returnAxiosResponse: true, signal };
  const response: AxiosResponse | false = await postRequest(`/v2/ride-book/data`, data, opts);
  if (response) {
    const responseForJsonSchema = { ...response.data, status: response.status };
    JSONValidationService.validateSchema(
      TripsOverviewDataSchema,
      responseForJsonSchema,
      'Trips Overview Data Schema'
    );
    if (response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getUserSettings = async (): Promise<any> => {
  const response: AxiosResponse | false = await getRequest(
    `/v1/user-settings/list_config_ride_book_v2`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    const responseForJsonSchema = { ...response.data, status: response.status };
    JSONValidationService.validateSchema(
      TripsOverviewSettingsSchema,
      responseForJsonSchema,
      'Trips Overview Settings Schema'
    );
    if (
      response.data &&
      response.status === 200 &&
      response.data.settings &&
      response.data.settings.value
    ) {
      return response.data.settings.value;
    }
    return false;
  }
  return false;
};

export const getEcodriveData = async (vehicleId: number, rideId: string): Promise<any> => {
  const response: AxiosResponse | false = await getRequest(
    `/v2/vehicle/${vehicleId}/ride/${rideId}`,
    { returnAxiosResponse: true }
  );
  if (response) {
    if (response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const arrayOrder = (
  array: ITripsOverviewColumn[],
  order: string[],
  key: string
): ITripsOverviewColumn[] => {
  array.sort((a, b) => {
    const A = a[key];
    const B = b[key];

    if (order.indexOf(A) < order.indexOf(B)) {
      return -1;
    }
    if (order.indexOf(A) > order.indexOf(B)) {
      return 1;
    }
    return 0;
  });

  const sortedArray = [
    ...array.filter((item) => item.key === LOCKED_KEY),
    ...array.filter((item) => item.key === TIMESTAMP_KEY),
    ...array.filter((item) => item.key === VEHICLE_KEY),
    ...array.filter((item) => !item.default && item.key !== ACTIONS_KEY),
    ...array.filter((item) => item.key === ACTIONS_KEY),
  ];

  return sortedArray;
};

export const getDataForFilter = async (
  columnId: string,
  customerIds?: string | null
): Promise<IFilterTable | false> => {
  const url = `/v2/ride-book/filter/${columnId}${customerIds ? `?customerId=${customerIds}` : ''}`;
  const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

  if (response && response.data) {
    return response.data;
  }

  return false;
};

export const saveTableSettingAndSummary = async (
  data: ITableSettingsData
): Promise<ITableSettingsResponse | null> => {
  const url = `/v1/user-settings/list_config_ride_book_v2/save`;
  const response: AxiosResponse | false = await postRequest(url, data, {
    returnAxiosResponse: true,
  });

  if (response && response.data && response.data.toast) {
    showMessage('toastr.warning', response.data.toast.message, 'warning', null);
    return null;
  }
  if (response && response.data && !response.data.toast) {
    return response.data.settings;
  }
  return null;
};

export const getTableSummaries = async (
  data: ITableSummariesFilter
): Promise<{ summaries: ITripsOverViewSummary[] } | null> => {
  const url = `/v2/ride-book/summaries`;
  const response: AxiosResponse | false = await postRequest(url, data, {
    returnAxiosResponse: true,
  });

  if (response && response.data && response.status === 200 && response.data.toast) {
    showMessage('toastr.warning', response.data.toast.message, 'warning', null);
    return null;
  }
  if (response && response.data && response.status === 200 && !response.data.toast) {
    return response.data;
  }
  return null;
};

export const getTagsData = async (
  type: string,
  customerId: string
): Promise<ITagItem[] | false> => {
  const response: AxiosResponse | false = await getRequest(
    `v2/ride-book/${type === 'usage' ? 'ride-tag-form' : 'parking-tag-form'}/${customerId}`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return response.data.form.inputs[0].values;
  }
  return false;
};

export const removeRide = async (rideId: string, vehicleId: string): Promise<boolean> => {
  const response: AxiosResponse | false = await deleteRequest(
    `v2/vehicle/${vehicleId}/ride/${rideId}`,
    { returnAxiosResponse: false }
  );
  if (response && (response.status === 200 || response.status === 202)) {
    return true;
  }
  return false;
};

export const getMultipleActions = async (
  checkedTableItems: ICheckedTableItems,
  filter: IFilterTripsOverview
): Promise<string[] | false> => {
  const input = {
    filter,
    idAllowlist:
      Object.keys(checkedTableItems.rideIds).length === 0 ? null : checkedTableItems.rideIds,
    idBlocklist:
      Object.keys(checkedTableItems.rideIdBlacklist).length === 0
        ? null
        : checkedTableItems.rideIdBlacklist,
    tsAllowlist: checkedTableItems.intervals.length === 0 ? null : checkedTableItems.intervals,
    tsBlocklist:
      checkedTableItems.intervalsBlacklist.length === 0
        ? null
        : checkedTableItems.intervalsBlacklist,
  };
  const response: AxiosResponse | false = await postRequest(`v2/ride-book/actions`, input, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    return Object.keys(response.data).filter((key) => response.data[key] === true);
  }
  return false;
};

export const getDrivers = async (customerId: string): Promise<IOption[] | false> => {
  const response: AxiosResponse | false = await getRequest(
    `v2/ride-book/driver-form/${customerId}`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return response.data.form.inputs[0].values;
  }
  return false;
};

export const changeDriver = async (
  values: FormikValues,
  rideData: ITripsOverViewRecord | null,
  checkedTableItems: ICheckedTableItems,
  filter: IFilterTripsOverview
): Promise<boolean> => {
  if (rideData) {
    values.idAllowlist = { [rideData.customer.id]: [rideData.id] };
  } else {
    values.idAllowlist =
      Object.keys(checkedTableItems.rideIds).length === 0 ? null : checkedTableItems.rideIds;
    values.idBlocklist =
      Object.keys(checkedTableItems.rideIdBlacklist).length === 0
        ? null
        : checkedTableItems.rideIdBlacklist;
    values.tsAllowlist =
      Object.keys(checkedTableItems.intervals).length === 0 ? null : checkedTableItems.intervals;
    values.tsBlocklist =
      Object.keys(checkedTableItems.intervalsBlacklist).length === 0
        ? null
        : checkedTableItems.intervalsBlacklist;
  }
  values.filter = filter;
  const response: AxiosResponse | false = await putRequest('v2/ride-book/driver', values, {
    returnAxiosResponse: true,
  });
  if (response && response.status === 200) {
    return true;
  }
  return false;
};

export const changeType = async (input: {
  isPrivate: boolean;
  records: { rideId: number; vehicleId: number }[];
}): Promise<boolean> => {
  const response: AxiosResponse | false = await putRequest('v1/ride-book/change-type', input, {
    returnAxiosResponse: true,
  });
  if (response && response.data) {
    return true;
  }
  return false;
};

export const editComment = async (
  comment: string,
  customerId: number,
  rideId: string,
  filter: IFilterTripsOverview,
  isParking?: boolean
): Promise<boolean> => {
  const data = {
    idAllowlist: { [customerId]: [rideId] },
    filter,
    comment,
  };
  const response: AxiosResponse | false = await putRequest(
    `v2/ride-book/comment/${isParking ? 'parking' : 'ride'}`,
    data,
    { returnAxiosResponse: true }
  );

  if (response && response.status === 200) {
    return true;
  }
  return false;
};

export const removeComment = async (
  customerId: number,
  rideId: string,
  filter: IFilterTripsOverview,
  isParking?: boolean
): Promise<boolean> => {
  const data = {
    idAllowlist: { [customerId]: [rideId] },
    filter,
  };
  const response: AxiosResponse | false = await deleteRequest(
    `v2/ride-book/comment/${isParking ? 'parking' : 'ride'}`,
    { returnAxiosResponse: true, data }
  );

  if (response && response.status === 200) {
    return true;
  }
  return false;
};

export const removeUsage = async (
  tagId: string[],
  customerId: number,
  rideId: string,
  filter: IFilterTripsOverview,
  isParking?: boolean
): Promise<boolean> => {
  const data = {
    idAllowlist: { [customerId]: [rideId] },
    tagId,
    filter,
  };
  const response: AxiosResponse | false = await deleteRequest(
    `v2/ride-book/tags/${isParking ? 'parking' : 'ride'}`,
    { returnAxiosResponse: true, data }
  );

  if (response && response.status === 200) {
    return true;
  }
  return false;
};

export const loadAnotherEvents = async (
  rideId: string,
  customerId: string,
  currentPage: number,
  information?: string,
  filters?: string[]
): Promise<IInfoRecord[] | false> => {
  let query = '';
  if ((filters && filters.length > 0) || information) {
    query = getEventsQuery(information || '', filters || []);
  }
  const response: AxiosResponse | false = await getRequest(
    `v2/ride/${rideId}/customer/${customerId}/info/${currentPage + 1}${query}`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return response.data.records;
  }
  return false;
};

export const loadFirstEvents = async (
  rideId: string,
  customerId: string,
  information: string,
  filters: string[]
): Promise<{ records: IInfoRecord[]; recordsCount: number } | false> => {
  const query = getEventsQuery(information || '', filters || []);
  const response: AxiosResponse | false = await getRequest(
    `v2/ride/${rideId}/customer/${customerId}/info/1${query}`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return response.data;
  }
  return false;
};

export const changeRidePrivacy = async (
  action: string,
  rideData: ITripsOverViewRecord | null,
  checkedTableItems: ICheckedTableItems,
  filter: IFilterTripsOverview
): Promise<boolean> => {
  let data = {};

  if (rideData) {
    data = {
      idAllowlist: { [rideData.customer.id]: [rideData.id] },
      isPrivate: TYPE_PRIVATE_ACTION === action,
      filter,
    };
  } else {
    data = {
      isPrivate: TYPE_PRIVATE_ACTION === action,
      filter,
      idAllowlist:
        Object.keys(checkedTableItems.rideIds).length === 0 ? null : checkedTableItems.rideIds,
      idBlocklist:
        Object.keys(checkedTableItems.rideIdBlacklist).length === 0
          ? null
          : checkedTableItems.rideIdBlacklist,
      tsAllowlist:
        Object.keys(checkedTableItems.intervals).length === 0 ? null : checkedTableItems.intervals,
      tsBlocklist:
        Object.keys(checkedTableItems.intervalsBlacklist).length === 0
          ? null
          : checkedTableItems.intervalsBlacklist,
    };
  }

  const response: AxiosResponse | false = await putRequest(`v2/ride-book/ride-privacy`, data, {
    returnAxiosResponse: true,
  });
  if (response && response.status === 200) {
    return true;
  }
  return false;
};

export const changeComment = async (
  values: FormikValues,
  rideData: ITripsOverViewRecord | null,
  checkedTableItems: ICheckedTableItems,
  type: string,
  filter: IFilterTripsOverview
): Promise<boolean> => {
  if (rideData) {
    values.idAllowlist = { [rideData.customer.id]: [rideData.id] };
  } else {
    values.idAllowlist =
      Object.keys(checkedTableItems.rideIds).length === 0 ? null : checkedTableItems.rideIds;
    values.idBlocklist =
      Object.keys(checkedTableItems.rideIdBlacklist).length === 0
        ? null
        : checkedTableItems.rideIdBlacklist;
    values.tsAllowlist =
      Object.keys(checkedTableItems.intervals).length === 0 ? null : checkedTableItems.intervals;
    values.tsBlocklist =
      Object.keys(checkedTableItems.intervalsBlacklist).length === 0
        ? null
        : checkedTableItems.intervalsBlacklist;
  }
  values.filter = filter;
  const response: AxiosResponse | false = await postRequest(
    `v2/ride-book/comment/${type === 'comment' ? 'ride' : 'parking'}`,
    values,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return true;
  }
  return false;
};

export const addUsage = async (
  values: FormikValues,
  rideData: ITripsOverViewRecord | null,
  checkedTableItems: ICheckedTableItems,
  type: string,
  filter: IFilterTripsOverview
): Promise<boolean> => {
  if (rideData) {
    values.idAllowlist = { [rideData.customer.id]: [rideData.id] };
  } else {
    values.idAllowlist =
      Object.keys(checkedTableItems.rideIds).length === 0 ? null : checkedTableItems.rideIds;
    values.idBlocklist =
      Object.keys(checkedTableItems.rideIdBlacklist).length === 0
        ? null
        : checkedTableItems.rideIdBlacklist;
    values.tsAllowlist =
      Object.keys(checkedTableItems.intervals).length === 0 ? null : checkedTableItems.intervals;
    values.tsBlocklist =
      Object.keys(checkedTableItems.intervalsBlacklist).length === 0
        ? null
        : checkedTableItems.intervalsBlacklist;
  }
  values.filter = filter;

  const response: AxiosResponse | false =
    type === 'usage'
      ? await postRequest('v2/ride-book/tags/ride', values, { returnAxiosResponse: true })
      : await postRequest('v2/ride-book/tags/parking', values, { returnAxiosResponse: true });
  if (response && response.data && response.data.toast && response.status === 200) {
    return true;
  }
  return false;
};

export const getCommentRemovePermisson = (customerId: number, locked: string): boolean => {
  return (
    !getLockedRideBoolean(locked, customerId, 'delete') &&
    PermissionsService.hasAccess(PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT, ['delete'], customerId)
  );
};

export const getCommentEditPermission = (customerId: number, locked: string): boolean => {
  return (
    !getLockedRideBoolean(locked, customerId, 'update') &&
    PermissionsService.hasAccess(PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT, ['update'], customerId)
  );
};

export const getUsageRemovePermission = (customerId: number, locked: string): boolean => {
  return (
    !getLockedRideBoolean(locked, customerId, 'delete') &&
    PermissionsService.hasAccess(PERMISSION_RIDE_INFO_TAGS_ASSIGMENT, ['delete'], customerId)
  );
};

export const getInitPage = (count?: number): number => {
  if (count) {
    return count % 20 === 0 ? count / 20 : Math.floor(count / 20) + 1;
  }
  return 1;
};

export const getDay = (date: string): string => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const d = new Date(date);
  return days[d.getDay()];
};

export const getTomorrow = (oldDate: string): string => {
  const result = new Date();
  const date = new Date(oldDate);
  result.setDate(date.getDate() + 1);
  return String(result);
};

export const getDayTimeFilter = async (): Promise<any | boolean> => {
  const responseDays: AxiosResponse | false = await getRequest(`/v2/ride-book/filter/days`, {
    returnAxiosResponse: true,
  });
  const responsetimeFrom: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/timeFrom`,
    { returnAxiosResponse: true }
  );
  const responsetimeTo: AxiosResponse | false = await getRequest(`/v2/ride-book/filter/timeTo`, {
    returnAxiosResponse: true,
  });
  const responseClosedInterval: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/closedInterval`,
    { returnAxiosResponse: true }
  );

  Promise.all([responseDays, responsetimeFrom, responsetimeTo, responseClosedInterval])
    .then((response) => {
      return response;
    })
    .catch((errors) => {
      // react on errors.
      console.error(errors);
    });
};

export const defineWeekdays = () => {
  const defaultLanguage = getDefaultLanguage();
  dayjs.extend(updateLocale);

  dayjs.updateLocale('en', {
    weekdaysMin: ['SU', 'MO', 'TO', 'WE', 'TH', 'FR', 'SA'],
  });
  dayjs.updateLocale('sk', {
    weekdays: ['Nedeľa', 'Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'],
    weekdaysMin: ['NE', 'PO', 'UT', 'ST', 'ŠT', 'PI', 'SO'],
  });
  dayjs.updateLocale('cs', {
    weekdays: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
    weekdaysMin: ['NE', 'PO', 'ÚT', 'ST', 'ČT', 'PÁ', 'SO'],
  });
  dayjs.updateLocale('hu', {
    weekdays: ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'],
    weekdaysMin: ['V', 'H', 'K', 'SZE', 'CS', 'P', 'SZO'],
  });

  const weekDays = getDayjsLocale(defaultLanguage);
  return weekDays;
};

export const convertTimeToSeconds = (time: Date) => {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  return hours * 3600 + minutes * 60 + seconds;
};

export const getAdvancedFilters = async () => {
  const announcementAlarm: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/announcementAlarm`,
    { returnAxiosResponse: true }
  );
  const announcementEvent: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/announcementEvent`,
    { returnAxiosResponse: true }
  );
  const announcementWaypoint: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/announcementWaypoint`,
    { returnAxiosResponse: true }
  );
  const ridebookPurpose: AxiosResponse | false = await getRequest(
    `/v2/ride-book/filter/ridebookPurpose`,
    { returnAxiosResponse: true }
  );
  const comment: AxiosResponse | false = await getRequest(`/v2/ride-book/filter/comment`, {
    returnAxiosResponse: true,
  });

  const response = Promise.all([
    announcementAlarm,
    announcementEvent,
    announcementWaypoint,
    ridebookPurpose,
    comment,
  ])
    .then(([responseAlarm, responseEvent, responseWaypoint, responsePurpose, responseComment]) => {
      return {
        responseAlarm: (responseAlarm as AxiosResponse).data,
        responseEvent: (responseEvent as AxiosResponse).data,
        responseWaypoint: (responseWaypoint as AxiosResponse).data,
        responsePurpose: (responsePurpose as AxiosResponse).data,
        responseComment: (responseComment as AxiosResponse).data,
      };
    })
    .catch((errors) => {
      // react on errors.
      console.error(errors);
    });
  if (response) {
    return response;
  }
  return false;
};

export const handleFilterKey = (actualObject, actualFilters: IIsActiveAdvanceFilter) => {
  const filteredKeys: any = Object.keys(actualFilters)
    .filter((key) => actualFilters[key])
    .reduce((result, key) => {
      result[key] = actualFilters[key];
      return result;
    }, {});

  const filteredObj = Object.keys(actualObject).reduce((result: any, key) => {
    if (filteredKeys.time) {
      result.timeFrom = actualObject.timeFrom;
      result.timeTo = actualObject.timeTo;
      result.closedInterval = actualObject.closedInterval;
    }

    if (key in filteredKeys) {
      result[key] = actualObject[key];
    }
    return result;
  }, {});

  return filteredObj;
};

export const handleChipOptionsCategory = (
  options: ITripsOverviewChipOption[],
  selectedValues: string[] | { [key: string]: string[] },
  categoriesName: string[]
) => {
  if (Object.values(selectedValues).length === 0) {
    return {
      [categoriesName[0]]: [],
      [categoriesName[1]]: options,
    };
  }

  const filteredSelectedValues: string[] = Array.isArray(selectedValues)
    ? selectedValues
    : Object.values(selectedValues).reduce((acc, val) => acc.concat(val), []);

  const result = options.reduce(
    (acc, item) => {
      if (filteredSelectedValues.includes(String(item.value))) {
        acc[categoriesName[0]].push(item);
      } else {
        acc[categoriesName[1]].push(item);
      }
      return acc;
    },
    {
      [categoriesName[0]]: [] as ITripsOverviewChipOption[],
      [categoriesName[1]]: [] as ITripsOverviewChipOption[],
    }
  );

  return result;
};

export const handleChipOptionsFixedCategory = (options: ITripsOverviewChipOption[]) => {
  const result = options.reduce(
    (acc, item) => {
      if (item.eventType) {
        if (!acc[item.eventType]) {
          acc[item.eventType] = [];
        }
        acc[item.eventType].push(item);
      }
      return acc;
    },
    {} as Record<string, ITripsOverviewChipOption[]>
  );

  return result;
};

export const findChipName = (
  options: ITripsOverviewChipOption[],
  selectedValue: string[] | { [key: string]: string[] }
): string => {
  const values = Array.isArray(selectedValue)
    ? selectedValue
    : Object.values(selectedValue).reduce((acc, val) => acc.concat(val), []);

  if (values.length > 1) {
    return String(values.length);
  }

  const findValueName = options.find((item) => values.includes(String(item.value)));

  return findValueName?.item || '';
};

export const getCustomerIdFromSelectedVehicles = (
  vehicles: IVehicles,
  selectedVehicles: number[]
): number[] => {
  const customerIds = [
    ...new Set(
      Object.entries(vehicles)
        .filter(([key]) => selectedVehicles.includes(Number(key)))
        .map(([, vehicle]) => vehicle.customerId)
    ),
  ];

  return customerIds;
};

export const findCustomerIdFromOptions = (
  options: ITripsOverviewChipOption[],
  selectedValues: string[] | { [key: string]: string[] }
): number[] => {
  const values = Array.isArray(selectedValues)
    ? selectedValues
    : Object.values(selectedValues).reduce((acc, val) => acc.concat(val), []);

  const customerIds = options
    .filter((item) => values.includes(String(item.value)))
    .map((item) => item.customerId);

  if (customerIds.every((value) => typeof value === 'number')) {
    return [...new Set(customerIds)] as number[];
  }
  return [];
};
